import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, useLanguage } from "@/context/AppProviders";
import { api } from "@/lib/api";
import { FadeIn, Overline } from "@/components/Kinetic";
import { Check, Lock, Play, MessageCircle, FileText } from "lucide-react";

const FALLBACK_COURSE_ID = "prise-de-parole";

export default function Formation() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [config, setConfig] = useState(null);
  const [access, setAccess] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/courses").then((r) => {
      const first = r.data.find((c) => c.id === FALLBACK_COURSE_ID) || r.data[0];
      if (first) api.get(`/courses/${first.id}`).then((d) => setCourse(d.data)).catch(() => {});
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      api.get("/me/courses").then((r) => setAccess(r.data.length > 0)).catch(() => {});
    }
  }, [user]);

  const title = course ? (lang === "fr" ? course.title_fr : course.title_en) : "";
  const hook = course ? (lang === "fr" ? course.hook_fr : course.hook_en) : "";
  const waLink = config ? `https://wa.me/${config.whatsapp}?text=${encodeURIComponent(t.formation.whatsappMsg)}` : "#";
  const totalVideos = course ? course.modules.reduce((n, m) => n + m.lessons.length, 0) : 0;

  return (
    <div className="pt-32 md:pt-40 pb-28 min-h-screen" data-testid="formation-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-8">
            <FadeIn><Overline>{t.formation.overline}</Overline></FadeIn>
            <FadeIn delay={0.1}>
              <h1 className="mt-6 font-display font-semibold tracking-tighter text-4xl md:text-6xl xl:text-7xl text-ink leading-[0.95]" data-testid="formation-title">
                {title || t.common.loading}
              </h1>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="mt-8 text-ink/60 leading-relaxed text-base md:text-lg max-w-2xl" data-testid="formation-hook">{hook}</p>
              <p className="mt-6 text-xs uppercase tracking-[0.25em] text-ink/40">
                {course ? course.modules.length : 4} {t.formation.modules} · {totalVideos} {t.formation.videos} · PDF
              </p>
            </FadeIn>

            {course && (
              <div className="mt-20">
                <FadeIn><Overline>{t.formation.program}</Overline></FadeIn>
                <div className="mt-10 space-y-0 border-t border-ink/10">
                  {course.modules.map((m, i) => (
                    <FadeIn key={m.id} delay={i * 0.05}>
                      <div className="py-10 border-b border-ink/10 grid md:grid-cols-12 gap-6" data-testid={`module-${m.id}`}>
                        <div className="md:col-span-3">
                          <span className="font-serif italic text-6xl text-ember/80 leading-none">{m.num}</span>
                          <h3 className="mt-4 font-display font-medium text-xl md:text-2xl text-ink tracking-tight">
                            {lang === "fr" ? m.title_fr : m.title_en}
                          </h3>
                          <p className="mt-3 text-sm text-ink/50 leading-relaxed">{lang === "fr" ? m.desc_fr : m.desc_en}</p>
                        </div>
                        <div className="md:col-span-8 md:col-start-5">
                          <ul className="divide-y divide-ink/5">
                            {m.module_type === "pdf"
                              ? (m.pdfs || []).map((p, j) => (
                                  <li key={p.id} className="py-4 flex items-center gap-4 text-ink/70" data-testid={`pdf-item-${p.id}`}>
                                    <span className="w-7 h-7 shrink-0 rounded-full border border-ink/15 flex items-center justify-center text-[11px] text-ink/40">{j + 1}</span>
                                    <span className="flex-1">{p.name}</span>
                                    {p.has_file ? <FileText size={14} className="text-ember shrink-0" /> : <Lock size={13} className="text-ink/25 shrink-0" />}
                                  </li>
                                ))
                              : m.lessons.map((l, j) => (
                                  <li key={l.id} className="py-4 flex items-center gap-4 text-ink/70" data-testid={`lesson-${l.id}`}>
                                    <span className="w-7 h-7 shrink-0 rounded-full border border-ink/15 flex items-center justify-center text-[11px] text-ink/40">{j + 1}</span>
                                    <span className="flex-1">{lang === "fr" ? l.title_fr : l.title_en}</span>
                                    {l.has_video ? <Play size={14} className="text-ember shrink-0" /> : <Lock size={13} className="text-ink/25 shrink-0" />}
                                  </li>
                                ))}
                          </ul>
                        </div>
                      </div>
                    </FadeIn>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ORDER BOX */}
          <div className="lg:col-span-4">
            <FadeIn delay={0.25}>
              <div className="lg:sticky lg:top-28 bg-surface border border-ink/10 p-8 shadow-[0_20px_60px_-30px_rgba(23,19,14,0.25)]">
                <div className="flex items-baseline gap-2">
                  <span className="font-serif italic text-6xl text-ink" data-testid="formation-price">{course ? course.price : 109}€</span>
                </div>
                <p className="mt-2 text-xs text-ink/45">{t.formation.accessNote}</p>

                <div className="my-7 h-px bg-ink/10" />
                <ul className="space-y-3 text-sm text-ink/60">
                  {[t.formation.inc1, t.formation.inc2, t.formation.inc3, t.formation.inc4].map((inc, i) => (
                    <li key={i} className="flex items-start gap-3"><Check size={15} className="text-ember mt-0.5 shrink-0" /> {inc}</li>
                  ))}
                </ul>

                <div className="mt-8">
                  {access ? (
                    <Link to="/espace-membre" data-testid="formation-access-btn" className="btn-glow block text-center bg-ember hover:bg-emberlight text-white px-6 py-4 text-sm tracking-wide transition-colors duration-300">
                      {t.formation.owned}
                    </Link>
                  ) : (
                    <div data-testid="order-container">
                      <p className="mb-4 border border-ember/30 bg-ember/5 px-4 py-3 text-xs text-ink/70 leading-relaxed">{t.formation.orderNote}</p>
                      {user === false && (
                        <button onClick={() => navigate("/auth?next=/formation")} data-testid="formation-login-btn" className="btn-glow mb-3 w-full bg-ink hover:bg-ember text-white px-6 py-4 text-sm tracking-wide transition-colors duration-300">
                          {t.formation.buy}
                        </button>
                      )}
                      <a
                        href={waLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="order-whatsapp-btn"
                        className="btn-glow w-full bg-ember hover:bg-emberlight text-white px-6 py-4 text-sm tracking-wide transition-colors duration-300 flex items-center justify-center gap-2"
                      >
                        <MessageCircle size={16} /> {t.formation.orderBtn}
                      </a>
                      {user === false && <p className="mt-4 text-xs text-ink/45 text-center">{t.formation.loginToBuy}</p>}
                    </div>
                  )}
                </div>
                <p className="mt-5 text-[11px] text-ink/35 text-center">{t.formation.secure}</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </div>
    </div>
  );
}
